import React, { useEffect, useState } from "react";
import CreateTravelPartner from "./CreateTravelPartner";
import Swal from "sweetalert2";
import EditTravelPartner from "./EditTravelPartner";
import { getAllTravelPartner, deleteTravelPartner } from "../../api/TravelPartner";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import "../../vendor/css/viewtravelpartner.css";

const ViewTravelPartner = () => {
  const [createPartnerModalShow, setCreatePartnerModalShow] = useState(false);
  const handleCreateModalClose = () => setCreatePartnerModalShow(false);
  const handleCreateModalShow = () => setCreatePartnerModalShow(true);

  const [editPartnerModalShow, setEditPartnerModalShow] = useState(false);
  const handleEditModalClose = () => setEditPartnerModalShow(false);
  const handleEditModalShow = () => setEditPartnerModalShow(true);

  const [partnerList, setPartnerList] = useState([]);
  const [selectedPartner, setSelectedPartner] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const partnersPerPage = 10;

  useEffect(() => {
    const getPartnerList = async () => {
      try {
        const response = await getAllTravelPartner();
        const partners = response?.travelPartner || (Array.isArray(response) ? response : []);
        setPartnerList(partners);
      } catch (error) {
        console.error("Error fetching partners:", error);
        Swal.fire("Error", "Failed to load travel partners", "error");
      } finally {
        setLoading(false);
      }
    };
    getPartnerList();
    const interval = setInterval(getPartnerList, 30000); // Refresh every 30 seconds
    return () => clearInterval(interval);
  }, []);

  // Filter partners based on search term
  const filteredPartners = partnerList.filter(partner =>
    partner.company_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Pagination logic
  const indexOfLastPartner = currentPage * partnersPerPage;
  const indexOfFirstPartner = indexOfLastPartner - partnersPerPage;
  const currentPartners = filteredPartners.slice(indexOfFirstPartner, indexOfLastPartner);
  const totalPages = Math.ceil(filteredPartners.length / partnersPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const handleDeletePartner = async (partnerToDelete) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    }).then((result) => {
      if (result.isConfirmed) {
        deleteTravelPartner(partnerToDelete._id || partnerToDelete.id)
          .then((res) => {
            if (res.status === 200) {
              Swal.fire("Deleted!", "Travel partner has been deleted.", "success");
              setPartnerList((prev) =>
                prev.filter((item) => item._id !== partnerToDelete._id && item.id !== partnerToDelete.id)
              );
            }
          })
          .catch((err) => {
            console.error("Delete failed:", err);
            Swal.fire("Error", "Failed to delete travel partner", "error");
          });
      }
    });
  };

  if (loading) {
    return (
      <div className="view-travel-partners">
        <div className="partners-header">
          <h1>Travel Partners Management</h1>
          <p>Manage and view all travel partners</p>
        </div>
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading travel partners...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="view-travel-partners">
      <div className="partners-header">
        <h1 style={{ fontFamily: 'serif' }}>Travel Partners Management</h1>
        <p style={{ fontFamily: 'serif' }}>Manage and view all travel partners</p>
      </div>

      <div className="partners-stats">
        <div className="stat-card">
          <div className="stat-icon">
            <i className="bx bx-group"></i>
          </div>
          <div className="stat-content">
            <h3>{partnerList.length}</h3>
            <p>Total Partners</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">
            <i className="bx bx-search"></i>
          </div>
          <div className="stat-content">
            <h3>{filteredPartners.length}</h3>
            <p>Filtered Results</p>
          </div>
        </div>
      </div>

      <div className="partners-controls">
        <div className="search-container">
          <i className="bx bx-search search-icon"></i>
          <input
            type="text"
            placeholder="Search by partner name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
        <button
          className="btn btn-primary add-partner-btn"
          onClick={handleCreateModalShow}
        >
          <i className="bx bx-plus"></i>
          Add Travel Partner
        </button>
      </div>

      <div className="partners-grid">
        {currentPartners.length > 0 ? (
          currentPartners.map((partner, index) => (
            <div key={partner._id || partner.id || index} className="partner-card">
              <div className="partner-image">
                <img
                  src={partner.image || "https://via.placeholder.com/300x200?text=No+Image"}
                  alt={partner.name || "Partner"}
                />
              </div>
              <div className="partner-info">
                <h3>{partner.company_name || "Unnamed Partner"}</h3>
                <div className="partner-actions">
                  <button
                    className="btn btn-primary edit-btn"
                    onClick={() => {
                      setSelectedPartner(partner);
                      handleEditModalShow();
                    }}
                    title="Edit Partner"
                  >
                    <i className="bx bx-edit"></i>
                    Edit
                  </button>
                  <button
                    className="btn btn-danger delete-btn"
                    onClick={() => handleDeletePartner(partner)}
                    title="Delete Partner"
                  >
                    <i className="bx bx-trash"></i>
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="no-data">
            <div className="no-data-content">
              <i className="bx bx-group-x"></i>
              <p>No travel partners found</p>
              <small>Try adjusting your search criteria</small>
            </div>
          </div>
        )}
      </div>

      {totalPages > 1 && (
        <div className="pagination">
          <button
            onClick={() => paginate(currentPage - 1)}
            disabled={currentPage === 1}
            className="pagination-btn"
          >
            <i className="bx bx-chevron-left"></i>
          </button>

          {Array.from({ length: totalPages }, (_, i) => (
            <button
              key={i + 1}
              onClick={() => paginate(i + 1)}
              className={`pagination-btn ${currentPage === i + 1 ? 'active' : ''}`}
            >
              {i + 1}
            </button>
          ))}

          <button
            onClick={() => paginate(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="pagination-btn"
          >
            <i className="bx bx-chevron-right"></i>
          </button>
        </div>
      )}

      {/* Create Travel Partner Modal */}
      <Modal
        show={createPartnerModalShow}
        onHide={handleCreateModalClose}
        className="partner-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>Add New Travel Partner</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <CreateTravelPartner
            onSuccess={(res) => {
              console.log(res);
              setCreatePartnerModalShow(false);
              // Refresh the list after successful creation
              const getPartnerList = async () => {
                try {
                  const response = await getAllTravelPartner();
                  const partners = response?.travelPartner || (Array.isArray(response) ? response : []);
                  setPartnerList(partners);
                } catch (error) {
                  console.error("Error refreshing partners:", error);
                }
              };
              getPartnerList();
            }}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCreateModalClose}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Edit Travel Partner Modal */}
      <Modal
        show={editPartnerModalShow}
        onHide={handleEditModalClose}
        className="partner-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>Update Travel Partner</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <EditTravelPartner
            partner={selectedPartner}
            onSuccess={(res) => {
              setPartnerList(res?.partners || res);
              setEditPartnerModalShow(false);
            }}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleEditModalClose}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ViewTravelPartner;
